import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBolt, FaBatteryHalf, FaMapMarkerAlt, FaHistory, FaInfoCircle, FaThermometerHalf } from 'react-icons/fa';
import { SMPSDevice } from '../utils/smpsTypes';
import { getStatusLabel, getBatteryModeLabel, getHealthColor } from '../utils/smpsConstants';

interface SMPSDeviceCardsProps {
  devices: SMPSDevice[];
  onDeviceClick: (device: SMPSDevice) => void;
}

const SMPSDeviceCards: React.FC<SMPSDeviceCardsProps> = ({ devices, onDeviceClick }) => {
  const navigate = useNavigate();

  const handleViewDetails = (e: React.MouseEvent, device: SMPSDevice) => {
    e.stopPropagation();
    navigate(`/smps-device-detail/${device.id}`);
  };

  const handleViewHistory = (e: React.MouseEvent, device: SMPSDevice) => {
    e.stopPropagation();
    navigate(`/smps-device-history/${device.id}`);
  };

  return (
    <div className="cards-container">
      {devices.map((device) => (
        <div key={device.id} className="device-card" onClick={() => onDeviceClick(device)}>
          <div className="device-card-header">
            <div className="device-info">
              <h3>{device.name.length > 25 ? `${device.name.substring(0, 25)}...` : device.name}</h3>
              <div className="device-location">
                <FaMapMarkerAlt />
                {device.station}
              </div>
            </div>
            <span
              className={`device-status-badge status-${device.status.toLowerCase()}`}
              style={{ backgroundColor: getHealthColor(device.status) }}
            >
              {getStatusLabel(device.status)}
            </span>
          </div>

          <div className="device-card-body">
            {/* DC Voltage */}
            <div className="metric-row">
              <div className="metric-info">
                <div className={`metric-icon ${device.dcVoltage > 0 ? 'active' : 'inactive'}`}>
                  <FaBolt />
                </div>
                <div className="metric-details">
                  <h4>DC Voltage</h4>
                  <span className="metric-value">{device.dcVoltage > 0 ? `${device.dcVoltage}V` : '-'}</span>
                </div>
              </div>
              <span className="metric-status">
                {device.dcVoltage > 0 ? (
                  device.dcVoltage >= 46 && device.dcVoltage <= 52 ? 'Normal' : 'Abnormal'
                ) : 'Offline'}
              </span>
            </div>

            {/* Load Current */}
            <div className="metric-row">
              <div className="metric-info">
                <div className={`metric-icon ${device.loadCurrent > 0 ? 'active' : 'inactive'}`}>
                  <FaBolt />
                </div>
                <div className="metric-details">
                  <h4>Load Current</h4>
                  <span className="metric-value">{device.loadCurrent > 0 ? `${device.loadCurrent}A` : '-'}</span>
                </div>
              </div>
              <span className="metric-status">{device.efficiency > 0 ? `${device.efficiency}% eff` : '-'}</span>
            </div>

            {/* Battery */}
            <div className="metric-row">
              <div className="metric-info">
                <div className={`metric-icon ${device.battery.capacity > 0 ? 'active' : 'inactive'}`}>
                  <FaBatteryHalf />
                </div>
                <div className="metric-details">
                  <h4>Battery</h4>
                  <span className="metric-value">{device.battery.capacity > 0 ? `${device.battery.capacity}%` : '-'}</span>
                </div>
              </div>
              <span className={`metric-status mode-${device.battery.mode.toLowerCase()}`}>
                {getBatteryModeLabel(device.battery.mode)}
              </span>
            </div>

            {/* Temperature */}
            <div className="metric-row">
              <div className="metric-info">
                <div className={`metric-icon ${device.temperature.ambient > 0 ? 'active' : 'inactive'}`}>
                  <FaThermometerHalf />
                </div>
                <div className="metric-details">
                  <h4>Temperature</h4>
                  <span className="metric-value">{device.temperature.ambient > 0 ? `${device.temperature.ambient}°C` : '-'}</span>
                </div>
              </div>
              <span className="metric-status">
                {device.temperature.ambient > 40 ? 'High' : device.temperature.ambient > 0 ? 'Normal' : '-'}
              </span>
            </div>
          </div>

          <div className="device-card-footer">
            <span>Last: {device.lastActivity}</span>
            <span>Rect: {device.rectifiers.online}/{device.rectifiers.total}</span>
          </div>

          <div className="device-card-actions">
            <button
              className="card-action-btn"
              onClick={(e) => handleViewDetails(e, device)}
              title="View Details"
            >
              <FaInfoCircle /> Details
            </button>
            <button
              className="card-action-btn"
              onClick={(e) => handleViewHistory(e, device)}
              title="View History"
            >
              <FaHistory /> History
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SMPSDeviceCards;
